import { useState, ChangeEvent, FormEvent, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ButtonSiguiente, Input } from "@/components/ui";
import { safeFetch } from "@/api/safeFetch";
import Alerta from "@/components/alert/Alerta";
import { AlertaType } from "@/types";

export default function CrearAfiliado() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const initialFormState = {
    nombre: "",
    apellidos: "",
    correo_electronico: "",
    numero_telefono: "",
    dni: "",
    codigo: "",
    porcentaje_descuento: 50,
    fecha_expiracion: "",
    contrasena: "",
    repetir_contrasena: "",
    direccion: {
      calle: "",
      localidad: "",
      provincia: "",
      codigo_postal: "",
      pais: ""
    }
  };

  const [form, setForm] = useState(initialFormState);
  const [loading, setLoading] = useState(false);
  const [errores, setErrores] = useState<{ [key: string]: boolean }>({});
  const [alerta, setAlerta] = useState<{ mensaje: string; type: AlertaType } | null>(null);
  const [showPass, setShowPass] = useState(false);
  const [showPass2, setShowPass2] = useState(false);

  const NO_UPPER = ["correo_electronico", "contrasena", "repetir_contrasena"];

  // Validación de campos obligatorios
  const validarFormulario = () => {
    const nuevosErrores: { [key: string]: boolean } = {};
    const obligatorios = [
      "nombre",
      "apellidos",
      "correo_electronico",
      "numero_telefono",
      "dni",
      "codigo",
      "porcentaje_descuento",
      "fecha_expiracion",
      "direccion.calle",
      "direccion.localidad",
      "direccion.provincia",
      "direccion.codigo_postal",
      "direccion.pais",
      "contrasena",
      "repetir_contrasena"
    ];

    obligatorios.forEach(field => {
      const value = field.includes(".")
        ? field.split(".").reduce((acc: any, key: string) => acc[key], form)
        : form[field as keyof typeof form];
      if (!value || value.toString().trim() === "") {
        nuevosErrores[field] = true;
      }
    });


    // ✅ AQUÍ la validación correcta
    if (form.contrasena !== form.repetir_contrasena) {
      nuevosErrores["repetir_contrasena"] = true;
    }


    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const finalValue = NO_UPPER.includes(name) ? value : value.toUpperCase();

    if (name.startsWith("direccion.")) {
      const key = name.split(".")[1];
      setForm(prev => ({
        ...prev,
        direccion: { ...prev.direccion, [key]: finalValue }
      }));
    } else {
      setForm(prev => ({ ...prev, [name]: finalValue }));
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const controller = new AbortController();
    const signal = controller.signal;

    if (!validarFormulario()) {
      setAlerta({ mensaje: "Por favor, rellena todos los campos obligatorios.", type: "error" });
      return;
    }

    setLoading(true);

    try {
      const resAfiliado = await safeFetch("/api/afiliados/crear", {
        method: "POST",
        body: JSON.stringify({
          nombre: form.nombre.trim(),
          apellidos: form.apellidos.trim(),
          correo_electronico: form.correo_electronico.trim(),
          numero_telefono: form.numero_telefono.trim(),
          dni: form.dni.trim(),
          contrasena: form.contrasena,
          direccion: {
            calle: form.direccion.calle.trim(),
            localidad: form.direccion.localidad.trim(),
            provincia: form.direccion.provincia.trim(),
            codigo_postal: form.direccion.codigo_postal.trim(),
            pais: form.direccion.pais.trim()
          }
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        signal
      });

      const data = await resAfiliado.json();
      const afiliadoId = data?.afiliadoId;

      if (!afiliadoId) throw new Error("No se recibió afiliadoId");

      const resCodigo = await safeFetch("/api/afiliados/asignar-codigo", {
        method: "POST",
        body: JSON.stringify({
          afiliadoId,
          codigo: form.codigo,
          porcentaje_descuento: Number(form.porcentaje_descuento),
          fecha_expiracion: form.fecha_expiracion
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        signal
      });

      if (!resCodigo.ok) throw new Error("Error asignando código");

      setAlerta({ mensaje: "Afiliado y código creados correctamente 🎉", type: "success" });
      setForm(initialFormState);
      setErrores({});
    } catch (err: any) {
      if (err.name !== "AbortError") {
        setAlerta({ mensaje: err.message || "Ocurrió un error.", type: "error" });
      }
    } finally {
      setLoading(false);
    }

    return () => controller.abort();
  };

  // Generar código con iniciales + 4 números
  const generarCodigoConNumeros = (iniciales: string) => {
    if (!iniciales.trim()) return "";
    const letras = iniciales.replace(/[^A-Za-z]/g, "").toUpperCase().slice(0, 4);
    const numeros = Math.floor(1000 + Math.random() * 9000);
    return `${letras}${numeros}`;
  };

  useEffect(() => {
    if (form.nombre || form.apellidos) {
      const iniciales = `${form.nombre} ${form.apellidos}`;
      const codigo = generarCodigoConNumeros(iniciales);

      setForm(prev => ({ ...prev, codigo }));
    }
  }, [form.nombre, form.apellidos]);


  useEffect(() => {
    return () => {
      setForm(initialFormState);
      setErrores({});
      setAlerta(null);
      setLoading(false);
    };
  }, []);


  return (
    <div className="w-full min-h-screen flex items-center justify-center bg-zinc-500 p-4 relative">

      {/* 🔙 Botón volver arriba izquierda */}
      <div className="absolute top-4 left-4">
        <ButtonSiguiente
          onClick={() => navigate("/afiliados")}
          className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition shadow"
        >
          ⬅ Volver
        </ButtonSiguiente>
      </div>

      <div className="w-full max-w-2xl bg-zinc-400 p-6 rounded-xl shadow-md border-zinc-600 border-r-4 border-t-2 shadow-green-700">
        <h2 className="text-2xl text-center font-bold mb-6">Crear Afiliado</h2>

        <form onSubmit={handleSubmit} className="grid grid-cols-12 gap-4">

          {/* Campos con error en rojo */}
          <Input
            type="text"
            name="nombre"
            placeholder="Nombre"
            value={form.nombre}
            onChange={handleChange}
            error={errores["nombre"]}
            className={`col-span-12 p-2 rounded uppercase
              ${errores["nombre"] ? "bg-red-100 placeholder:text-zinc-400" : "bg-white placeholder:text-zinc-400"}
            `}
          />

          <Input
            type="text"
            name="apellidos"
            placeholder="Apellidos"
            value={form.apellidos}
            onChange={handleChange}
            error={errores["apellidos"]}
            className={`col-span-12 p-2 rounded uppercase
              ${errores["apellidos"] ? "bg-red-100 placeholder:text-zinc-400" : "bg-white placeholder:text-zinc-400"}
            `}
          />

          <Input
            type="text"
            name="dni"
            placeholder="DNI/NIE"
            value={form.dni}
            onChange={handleChange}
            error={errores["dni"]}
            className={`col-span-12 p-2 rounded uppercase
              ${errores["dni"] ? "bg-red-100 placeholder:text-zinc-400" : "bg-white placeholder:text-zinc-400"}
            `}
          />

          <Input
            type="email"
            name="correo_electronico"
            placeholder="Correo electrónico"
            value={form.correo_electronico}
            onChange={handleChange}
            error={errores["correo_electronico"]}
            className={`col-span-12 p-2 rounded
              ${errores["correo_electronico"] ? "bg-red-100 placeholder:text-zinc-400" : "bg-white placeholder:text-zinc-400"}
            `}
          />

          <div className="relative col-span-12">
            <Input
              type={showPass ? "text" : "password"}
              name="contrasena"
              placeholder="Contraseña"
              value={form.contrasena}
              onChange={handleChange}
              error={errores["contrasena"]}
              className={`w-full p-2 pr-10 rounded ${errores["contrasena"] ? "bg-red-100" : "bg-white"
                }`}
            />

            {/* ICONO OJO */}
            <button
              type="button"
              onClick={() => setShowPass(!showPass)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-900"
            >
              {showPass ? (
                /* OJO ABIERTO */
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              ) : (
                /* OJO CERRADO */
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17.94 17.94A10.94 10.94 0 0112 20c-7 0-11-8-11-8a21.77 21.77 0 014.62-5.94" />
                  <path d="M1 1l22 22" />
                </svg>
              )}
            </button>
          </div>

          <div className="relative col-span-12">
            <Input
              type={showPass2 ? "text" : "password"}
              name="repetir_contrasena"
              placeholder="Repetir contraseña"
              value={form.repetir_contrasena}
              onChange={handleChange}
              error={errores["repetir_contrasena"]}
              className={`w-full p-2 pr-10 rounded ${errores["repetir_contrasena"] ? "bg-red-100" : "bg-white"
                }`}
            />

            {/* ICONO OJO */}
            <button
              type="button"
              onClick={() => setShowPass2(!showPass2)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-900"
            >
              {showPass2 ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17.94 17.94A10.94 10.94 0 0112 20c-7 0-11-8-11-8a21.77 21.77 0 014.62-5.94" />
                  <path d="M1 1l22 22" />
                </svg>
              )}
            </button>
          </div>


          <Input
            type="text"
            name="numero_telefono"
            placeholder="Teléfono"
            value={form.numero_telefono}
            onChange={handleChange}
            error={errores["numero_telefono"]}
            className={`col-span-12 p-2 rounded uppercase
              ${errores["numero_telefono"] ? "bg-red-100 placeholder:text-zinc-400" : "bg-white placeholder:text-zinc-400"}
            `}
          />

          <Input
            type="text"
            name="codigo"
            placeholder="Iniciales (ej: JUPE)"
            value={form.codigo}
            onChange={handleChange}
            onBlur={() => setForm(prev => ({ ...prev, codigo: generarCodigoConNumeros(prev.codigo) }))}
            error={errores["codigo"]}
            className={`col-span-6 p-2 rounded uppercase
              ${errores["codigo"] ? "bg-red-100 placeholder:text-zinc-400" : "bg-white placeholder:text-zinc-400"}
            `}
          />

          <Input
            type="number"
            name="porcentaje_descuento"
            placeholder="% Descuento"
            value={form.porcentaje_descuento}
            onChange={handleChange}
            error={errores["porcentaje_descuento"]}
            className={`col-span-6 p-2 rounded uppercase appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none
              ${errores["porcentaje_descuento"] ? "bg-red-100 placeholder:text-zinc-400" : "bg-white placeholder:text-zinc-400"}
            `}
          />

          <label htmlFor="fecha_expiracion" className="flex ml-2 -mb-4 text-sm text-gray-600 whitespace-nowrap">Fecha de expiración código</label>
          <Input
            type="date"
            name="fecha_expiracion"
            value={form.fecha_expiracion}
            onChange={handleChange}
            error={errores["fecha_expiracion"]}
            className={`col-span-12 p-2 rounded uppercase !w-full h-9  appearance-none
              ${errores["fecha_expiracion"] ? "bg-red-100 placeholder:text-zinc-400" : "bg-white placeholder:text-zinc-400"}
            `}
          />

          {/* Dirección */}
          <Input
            type="text"
            name="direccion.calle"
            placeholder="Calle"
            value={form.direccion.calle}
            onChange={handleChange}
            error={errores["direccion.calle"]}
            className={`col-span-12 p-2 rounded uppercase
              ${errores["direccion.calle"] ? "bg-red-100 placeholder:text-zinc-400" : "bg-white placeholder:text-zinc-400"}
            `}
          />

          <Input
            type="text"
            name="direccion.localidad"
            placeholder="Localidad"
            value={form.direccion.localidad}
            onChange={handleChange}
            error={errores["direccion.localidad"]}
            className={`col-span-6 p-2 rounded uppercase
              ${errores["direccion.localidad"] ? "bg-red-100 placeholder:text-zinc-400" : "bg-white placeholder:text-zinc-400"}
            `}
          />

          <Input
            type="text"
            name="direccion.provincia"
            placeholder="Provincia"
            value={form.direccion.provincia}
            onChange={handleChange}
            error={errores["direccion.provincia"]}
            className={`col-span-6 p-2 rounded uppercase
              ${errores["direccion.provincia"] ? "bg-red-100 placeholder:text-zinc-400" : "bg-white placeholder:text-zinc-400"}
            `}
          />

          <Input
            type="text"
            name="direccion.codigo_postal"
            placeholder="Código postal"
            value={form.direccion.codigo_postal}
            onChange={handleChange}
            error={errores["direccion.codigo_postal"]}
            className={`col-span-6 p-2 rounded uppercase
              ${errores["direccion.codigo_postal"] ? "bg-red-100 placeholder:text-zinc-400" : "bg-white placeholder:text-zinc-400"}
            `}
          />

          <Input
            type="text"
            name="direccion.pais"
            placeholder="País"
            value={form.direccion.pais}
            onChange={handleChange}
            error={errores["direccion.pais"]}
            className={`col-span-6 p-2 rounded uppercase
              ${errores["direccion.pais"] ? "bg-red-100 placeholder:text-zinc-400" : "bg-white placeholder:text-zinc-400"}
            `}
          />

          <ButtonSiguiente type="submit" className="col-span-12 bg-green-600 text-white p-3 rounded hover:bg-green-700 transition">
            {loading ? "Creando..." : "Crear Afiliado"}
          </ButtonSiguiente>
        </form>
      </div>

      {/* Alerta flotante */}
      {alerta && (
        <Alerta
          mensaje={alerta.mensaje}
          type={alerta.type}
          onClose={() => setAlerta(null)}
        />
      )}
    </div>
  );

}
